import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { usePermissions } from './hooks/usePermissions';
import { syncPendingSales, refreshCache } from './services/syncService';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Purchases from './pages/Purchases';
import Customers from './pages/Customers';
import Sales from './pages/Sales';
import Finances from './pages/Finances';
import Pricing from './pages/Pricing';
import PaymentVerify from './pages/PaymentVerify';
import Team from './pages/Team';
import InviteAccept from './pages/InviteAccept';
import Onboarding from './pages/Onboarding';
import Admin from './pages/Admin';
import Developer from './pages/Developer';
import POS from './pages/POS';
import Analytics from './pages/Analytics';
import Debts from './pages/Debts';
import InventoryIntelligence from './pages/InventoryIntelligence';
import CRM from './pages/CRM';
import FinancialIntelligence from './pages/FinancialIntelligence';
import Reports from './pages/Reports';
import AIInsights from './pages/AIInsights';
import StaffManagement from './pages/StaffManagement';
import SecurityAudit from './pages/SecurityAudit';
import Settings from './pages/Settings';
import Branches from './pages/Branches';
import ARAPAging from './pages/ARAPAging';
import SalesOrders from './pages/SalesOrders';
import MonthClose from './pages/MonthClose';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Download from './pages/Download';
import InstallPrompt from './components/InstallPrompt';
import VerifyEmail from './pages/VerifyEmail';
import PrivacyBanner from './components/PrivacyBanner';

// Resets the error boundary on every route change, so a render crash on one
// page (e.g. bad API data) can't permanently freeze navigation to every
// other page until a hard reload.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

// Auto-sync offline sales when connection is restored
function SyncManager() {
  const { user } = useAuth();
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    if (!isOnline || !user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    syncPendingSales(token).then(({ synced, failed }) => {
      if (synced > 0) console.log(`[sync] ${synced} offline sale(s) synced`);
      if (failed > 0) console.warn(`[sync] ${failed} sale(s) failed to sync`);
    });
    refreshCache(token);
  }, [isOnline, user]);

  return null;
}

const GROWTH_PLANS     = ['growth', 'enterprise', 'pro', 'business'];
const ENTERPRISE_PLANS = ['enterprise', 'business'];

// Plan-gated route — redirects to /pricing if user's plan isn't in the allowed list
function PlanRoute({ plans, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.isAdmin) return children;
  const subscriptionActive = !user.subscriptionEndDate || new Date(user.subscriptionEndDate) > new Date();
  if (!subscriptionActive || !plans.includes(user.plan || 'trial')) return <Navigate to="/pricing?upgrade=required" replace />;
  return children;
}

// Owner-only route — redirects non-owners to /dashboard
function OwnerRoute({ children }) {
  const { user, loading } = useAuth();
  const { role } = usePermissions();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role !== 'owner') return <Navigate to="/dashboard" replace />;
  return children;
}

function PermissionRoute({ permission, children }) {
  const { user, loading } = useAuth();
  const { can } = usePermissions();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(permission)) return <Navigate to="/dashboard" replace />;
  return children;
}

// "/" → Landing for guests, /dashboard for logged-in users
function HomeRoute() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);
  if (loading) return null;
  if (user) return null;
  return <Landing />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <SyncManager />
          <RouteErrorBoundary>
          <Routes>
            <Route path="/"                element={<HomeRoute />} />
            <Route path="/login"           element={<Login key="login" />} />
            <Route path="/register"        element={<Login key="register" defaultTab="register" />} />
            <Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/pos"             element={<ProtectedRoute><PermissionRoute permission="pos:use"><POS /></PermissionRoute></ProtectedRoute>} />
            <Route path="/dashboard"       element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/inventory"       element={<ProtectedRoute><PermissionRoute permission="inventory:view"><Inventory /></PermissionRoute></ProtectedRoute>} />
            <Route path="/purchases"       element={<ProtectedRoute><PermissionRoute permission="purchases:view"><Purchases /></PermissionRoute></ProtectedRoute>} />
            <Route path="/customers"       element={<ProtectedRoute><PermissionRoute permission="customers:view"><Customers /></PermissionRoute></ProtectedRoute>} />
            <Route path="/sales"           element={<ProtectedRoute><PermissionRoute permission="sales:view"><Sales /></PermissionRoute></ProtectedRoute>} />
            <Route path="/orders"          element={<ProtectedRoute><PermissionRoute permission="sales:view"><SalesOrders /></PermissionRoute></ProtectedRoute>} />
            <Route path="/finances"        element={<ProtectedRoute><PermissionRoute permission="finances:view"><Finances /></PermissionRoute></ProtectedRoute>} />
            <Route path="/analytics"       element={<ProtectedRoute><PlanRoute plans={GROWTH_PLANS}><PermissionRoute permission="analytics:view"><Analytics /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/debts"           element={<ProtectedRoute><PermissionRoute permission="debt:view"><Debts /></PermissionRoute></ProtectedRoute>} />
            <Route path="/inventory/intelligence" element={<ProtectedRoute><PlanRoute plans={ENTERPRISE_PLANS}><PermissionRoute permission="inventory:activity:view"><InventoryIntelligence /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/crm"                   element={<ProtectedRoute><PlanRoute plans={GROWTH_PLANS}><PermissionRoute permission="crm:view"><CRM /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/financials"            element={<ProtectedRoute><PlanRoute plans={GROWTH_PLANS}><PermissionRoute permission="finances:view"><FinancialIntelligence /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/reports"              element={<ProtectedRoute><PermissionRoute permission="reports:view"><Reports /></PermissionRoute></ProtectedRoute>} />
            <Route path="/aging"               element={<ProtectedRoute><PlanRoute plans={GROWTH_PLANS}><PermissionRoute permission="reports:view"><ARAPAging /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/close"               element={<OwnerRoute><PlanRoute plans={GROWTH_PLANS}><MonthClose /></PlanRoute></OwnerRoute>} />
            <Route path="/ai"                   element={<ProtectedRoute><PlanRoute plans={ENTERPRISE_PLANS}><PermissionRoute permission="ai:view"><AIInsights /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/staff"               element={<ProtectedRoute><PlanRoute plans={GROWTH_PLANS}><PermissionRoute permission="staff:view"><StaffManagement /></PermissionRoute></PlanRoute></ProtectedRoute>} />
            <Route path="/pricing"         element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
            <Route path="/payment/verify"  element={<ProtectedRoute><PaymentVerify /></ProtectedRoute>} />
            <Route path="/team"            element={<ProtectedRoute><Team /></ProtectedRoute>} />
            <Route path="/developer"       element={<ProtectedRoute><Developer /></ProtectedRoute>} />
            <Route path="/onboarding"      element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/admin"           element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/security"        element={<OwnerRoute><PlanRoute plans={ENTERPRISE_PLANS}><SecurityAudit /></PlanRoute></OwnerRoute>} />
            <Route path="/settings"        element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/branches"        element={<OwnerRoute><Branches /></OwnerRoute>} />
            <Route path="/verify-email"    element={<VerifyEmail />} />
            <Route path="/privacy"         element={<PrivacyPolicy />} />
            <Route path="/download"        element={<Download />} />
            <Route path="/invite/accept"   element={<InviteAccept />} />
            <Route path="*"               element={<Navigate to="/" replace />} />
          </Routes>
          </RouteErrorBoundary>
          <InstallPrompt />
          <PrivacyBanner />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
